// Shared catalog source of truth via backend API.
import axios from 'axios';
import { PRODUCTS as SEED_PRODUCTS, CATEGORIES as SEED_CATEGORIES } from '../data/mock';
import { useCallback, useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const CATALOG_API = `${BACKEND_URL}/api/catalog`;
const ADMIN_TOKEN = 'EcomSeekho56:Ecom@567';

const adminHeaders = () => ({ headers: { 'X-Admin-Auth': ADMIN_TOKEN } });

export const catalogApi = {
  async list() {
    const [p, c] = await Promise.all([
      axios.get(`${CATALOG_API}/products`).catch(() => ({ data: [] })),
      axios.get(`${CATALOG_API}/categories`).catch(() => ({ data: [] })),
    ]);
    return { products: p.data || [], categories: c.data || [] };
  },
  async listDeleted() {
    const r = await axios.get(`${CATALOG_API}/products/deleted`, adminHeaders());
    return r.data;
  },
  async status() {
    const r = await axios.get(`${CATALOG_API}/status`);
    return r.data;
  },
  async createProduct(p) {
    const r = await axios.post(`${CATALOG_API}/products`, p, adminHeaders());
    return r.data;
  },
  async updateProduct(id, p) {
    const r = await axios.put(`${CATALOG_API}/products/${id}`, p, adminHeaders());
    return r.data;
  },
  async softDelete(id) {
    await axios.delete(`${CATALOG_API}/products/${id}`, adminHeaders());
  },
  async restore(id) {
    await axios.post(`${CATALOG_API}/products/${id}/restore`, {}, adminHeaders());
  },
  async permanentDelete(id) {
    await axios.delete(`${CATALOG_API}/products/${id}/permanent`, adminHeaders());
  },
  async bulkUpsertProducts(products, replaceAll = false) {
    const r = await axios.post(`${CATALOG_API}/products/bulk`, { products, replace_all: replaceAll }, adminHeaders());
    return r.data;
  },
  async createCategory(cat) {
    const r = await axios.post(`${CATALOG_API}/categories`, cat, adminHeaders());
    return r.data;
  },
  async deleteCategory(id) {
    await axios.delete(`${CATALOG_API}/categories/${id}`, adminHeaders());
  },
  async bulkUpsertCategories(categories, replaceAll = false) {
    await axios.post(`${CATALOG_API}/categories/bulk`, { categories, replace_all: replaceAll }, adminHeaders());
  },
  async seedIfEmpty() {
    const status = await this.status();
    if (!status.seeded) {
      await this.bulkUpsertCategories(SEED_CATEGORIES, true);
      // Chunked insert to avoid huge payload
      const chunk = 100;
      for (let i = 0; i < SEED_PRODUCTS.length; i += chunk) {
        await this.bulkUpsertProducts(SEED_PRODUCTS.slice(i, i + chunk), i === 0);
      }
    }
  },
};

export const useCatalog = () => {
  const [state, setState] = useState({ products: [], categories: [], loading: true });

  const refresh = useCallback(async () => {
    try {
      const { products, categories } = await catalogApi.list();
      setState({
        products: products.length ? products : SEED_PRODUCTS,
        categories: categories.length ? categories : SEED_CATEGORIES,
        loading: false,
      });
    } catch (e) {
      setState({ products: SEED_PRODUCTS, categories: SEED_CATEGORIES, loading: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onCustom = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('es:catalog-changed', onCustom);
    const interval = setInterval(refresh, 8000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('es:catalog-changed', onCustom);
      clearInterval(interval);
    };
  }, [refresh]);

  return { ...state, refresh };
};

export const notifyCatalogChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent('es:catalog-changed'));
  } catch {
    // ignore
  }
};
